import type {
  InsuranceClaim,
  InsuranceClaimStatus,
  InsuranceProvider,
  PatientInsurance,
} from '@pharmaconnect/types'

import { supabase } from '../lib/supabase'

import { logAudit } from './audit'

export interface PatientInsuranceWithProvider extends PatientInsurance {
  provider: InsuranceProvider
}

export interface InsuranceClaimWithRelations extends InsuranceClaim {
  provider: Pick<InsuranceProvider, 'id' | 'name' | 'short_name'>
  policy_number: string
  patient_name: string | null
  sale_total: number
  sale_created_at: string
}

export const listInsuranceProviders = async (
  activeOnly: boolean = true,
): Promise<InsuranceProvider[]> => {
  let query = supabase.from('insurance_providers').select('*').order('name', { ascending: true })
  if (activeOnly) query = query.eq('is_active', true)
  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as InsuranceProvider[]
}

export const upsertInsuranceProvider = async (
  payload: Partial<InsuranceProvider> & { name: string },
): Promise<InsuranceProvider> => {
  const { data, error } = await supabase
    .from('insurance_providers')
    .upsert(payload as never)
    .select('*')
    .single()
  if (error) throw error
  return data as InsuranceProvider
}

export const setInsuranceProviderActive = async (
  id: string,
  isActive: boolean,
): Promise<void> => {
  const { error } = await supabase
    .from('insurance_providers')
    .update({ is_active: isActive } as never)
    .eq('id', id)
  if (error) throw error
}

export const fetchPatientInsurances = async (
  patientId: string,
  activeOnly: boolean = true,
): Promise<PatientInsuranceWithProvider[]> => {
  let query = supabase
    .from('patient_insurances')
    .select('*, provider:insurance_providers(*)')
    .eq('patient_id', patientId)
    .order('is_primary', { ascending: false })
    .order('created_at', { ascending: false })
  if (activeOnly) query = query.eq('is_active', true)
  const { data, error } = await query
  if (error) throw error
  return (data ?? []) as PatientInsuranceWithProvider[]
}

export interface UpsertPatientInsuranceInput {
  id?: string
  patient_id: string
  insurance_provider_id: string
  policy_number: string
  coverage_rate: number
  valid_from?: string | null
  valid_until?: string | null
  is_primary?: boolean
  is_active?: boolean
}

export const upsertPatientInsurance = async (
  input: UpsertPatientInsuranceInput,
): Promise<PatientInsurance> => {
  const { data, error } = await supabase
    .from('patient_insurances')
    .upsert(input as never)
    .select('*')
    .single()
  if (error) throw error
  return data as PatientInsurance
}

export const deletePatientInsurance = async (id: string): Promise<void> => {
  const { error } = await supabase.from('patient_insurances').delete().eq('id', id)
  if (error) throw error
}

export interface CreateInsuranceClaimInput {
  pharmacy_id: string
  sale_id: string
  patient_insurance_id: string
  insurance_provider_id: string
  total_amount: number
  coverage_rate: number
}

/**
 * Crée une créance assurance liée à une vente — calcule automatiquement
 * le découpage part assurance / part patient. Met à jour sales.insurance_claim_id.
 */
export const createInsuranceClaim = async (
  input: CreateInsuranceClaimInput,
): Promise<InsuranceClaim> => {
  const insurance_amount = Math.round((input.total_amount * input.coverage_rate) / 100 / 100) * 100
  const patient_amount = input.total_amount - insurance_amount

  const { data, error } = await supabase
    .from('insurance_claims')
    .insert({
      pharmacy_id: input.pharmacy_id,
      sale_id: input.sale_id,
      patient_insurance_id: input.patient_insurance_id,
      insurance_provider_id: input.insurance_provider_id,
      total_amount: input.total_amount,
      insurance_amount,
      patient_amount,
      coverage_rate: input.coverage_rate,
      status: 'pending',
    } as never)
    .select('*')
    .single()
  if (error) throw error
  const claim = data as InsuranceClaim

  // Lier la vente à la créance pour visualisation rapide
  await supabase
    .from('sales')
    .update({ insurance_claim_id: claim.id } as never)
    .eq('id', input.sale_id)

  await logAudit({
    action: 'insurance_claim.create',
    entity_type: 'insurance_claim',
    entity_id: claim.id,
    changes: {
      sale_id: input.sale_id,
      coverage_rate: input.coverage_rate,
      insurance_amount,
      patient_amount,
    },
  })

  return claim
}

export const listInsuranceClaims = async (
  pharmacyId: string,
  status?: InsuranceClaimStatus,
): Promise<InsuranceClaimWithRelations[]> => {
  let query = supabase
    .from('insurance_claims')
    .select(
      [
        '*',
        'provider:insurance_providers(id, name, short_name)',
        'patient_insurance:patient_insurances(policy_number, patient:user_profiles(full_name))',
        'sale:sales(total, created_at)',
      ].join(','),
    )
    .eq('pharmacy_id', pharmacyId)
    .order('created_at', { ascending: false })

  if (status) query = query.eq('status', status)

  const { data, error } = await query
  if (error) throw error

  type RawRow = InsuranceClaim & {
    provider: { id: string; name: string; short_name: string | null }
    patient_insurance: {
      policy_number: string
      patient: { full_name: string } | null
    } | null
    sale: { total: number; created_at: string } | null
  }

  return ((data ?? []) as RawRow[]).map((row) => ({
    ...row,
    provider: row.provider,
    policy_number: row.patient_insurance?.policy_number ?? '',
    patient_name: row.patient_insurance?.patient?.full_name ?? null,
    sale_total: row.sale?.total ?? row.total_amount,
    sale_created_at: row.sale?.created_at ?? row.created_at,
  }))
}

export const updateClaimStatus = async (
  id: string,
  payload: {
    status: InsuranceClaimStatus
    paid_amount?: number
    rejection_reason?: string
    notes?: string
  },
): Promise<InsuranceClaim> => {
  const update: Record<string, unknown> = { status: payload.status }
  if (payload.status === 'submitted') update.submitted_at = new Date().toISOString()
  if (payload.status === 'paid') {
    update.paid_at = new Date().toISOString()
    if (payload.paid_amount !== undefined) update.paid_amount = payload.paid_amount
  }
  if (payload.status === 'rejected' && payload.rejection_reason) {
    update.rejection_reason = payload.rejection_reason
  }
  if (payload.notes !== undefined) update.notes = payload.notes

  const { data, error } = await supabase
    .from('insurance_claims')
    .update(update as never)
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  const claim = data as InsuranceClaim

  await logAudit({
    action: `insurance_claim.${payload.status}`,
    entity_type: 'insurance_claim',
    entity_id: id,
    changes: payload,
  })

  return claim
}

export interface InsurancePendingSummary {
  count_pending: number
  total_pending: number
  count_submitted: number
  total_submitted: number
  oldest_pending_days: number
}

export const fetchInsurancePendingSummary = async (
  pharmacyId: string,
): Promise<InsurancePendingSummary> => {
  const { data, error } = await supabase.rpc(
    'insurance_pending_summary' as never,
    { p_pharmacy_id: pharmacyId } as never,
  )
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as InsurancePendingSummary | null
  return (
    row ?? {
      count_pending: 0,
      total_pending: 0,
      count_submitted: 0,
      total_submitted: 0,
      oldest_pending_days: 0,
    }
  )
}
